import * as React from "react";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";

import ErrorBanner from "../../components/ErrorBanner";
import LoadingIndicator from "../../components/LoadingIndicator";

import {
  useQueryGetBrokerCommsCarriers,
  useMutateScheduleBrokerCommsCarriers,
  useMutateUpdateBrokerCommsCarrier,
  useQueryGetBrokerTemplates,
} from "../../data/services/brokerCommsService";
import { useQueryGetCarrierList } from "../../data/services/carrierService";

const SEND_MODES = [
  { text: "Immediate", value: "Immediate" },
  { text: "Scheduled", value: "Scheduled" },
  { text: "OnCarrierRelease", value: "OnCarrierRelease" },
];

const StatusBadge = ({ status }) => {
  const colorMap = {
    Pending: "#e6a817",
    Sent: "#37a000",
    Cancelled: "#6c757d",
    Failed: "#c4230a",
  };
  const bg = colorMap[status] || "#6c757d";
  return (
    <span
      style={{
        backgroundColor: bg,
        color: "#fff",
        borderRadius: 12,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {status || "Unknown"}
    </span>
  );
};

const BrokerCommsByCarrierGrid = ({ waveId, oePeriod }) => {
  const [dataState, setDataState] = React.useState({ take: 10, skip: 0 });
  const [gridData, setGridData] = React.useState([]);
  const [masterData, setMasterData] = React.useState([]);
  const [selectedCarrierIds, setSelectedCarrierIds] = React.useState([]);
  const [editedRows, setEditedRows] = React.useState({});

  const {
    isLoading: carriersLoading,
    data: brokerCommsCarriersData,
    refetch: refetchCarriers,
  } = useQueryGetBrokerCommsCarriers(waveId);

  const { isLoading: carrierListLoading, data: carrierListData } =
    useQueryGetCarrierList();

  const { data: templatesData } = useQueryGetBrokerTemplates("Broker", null);

  const scheduleMutation = useMutateScheduleBrokerCommsCarriers(waveId);
  const updateMutation = useMutateUpdateBrokerCommsCarrier(waveId);

  const existingCarrierIds = React.useMemo(() => {
    if (!masterData) return new Set();
    return new Set(masterData.map((r) => r.carrierId));
  }, [masterData]);

  const availableCarriers = React.useMemo(() => {
    if (!carrierListData?.data) return [];
    return carrierListData.data.filter(
      (c) => !existingCarrierIds.has(c.carrierId)
    );
  }, [carrierListData, existingCarrierIds]);

  const carrierMap = React.useMemo(() => {
    const map = {};
    if (carrierListData?.data) {
      carrierListData.data.forEach((c) => {
        map[c.carrierId] = c;
      });
    }
    return map;
  }, [carrierListData]);

  const templateOptions = React.useMemo(() => {
    if (!templatesData?.data) return [];
    return templatesData.data.map((t) => ({
      text: t.name,
      value: t.brokerTemplateId,
    }));
  }, [templatesData]);

  React.useEffect(() => {
    if (!carriersLoading && brokerCommsCarriersData?.data) {
      const rows = brokerCommsCarriersData.data;
      setMasterData(rows);
      setGridData(process(rows.slice(0), dataState));
    }
  }, [carriersLoading, brokerCommsCarriersData]);

  React.useEffect(() => {
    if (masterData.length > 0) {
      setGridData(process(masterData.slice(0), dataState));
    }
  }, [dataState, masterData]);

  const dataStateChange = (event) => {
    setGridData(process(masterData.slice(0), event.dataState));
    setDataState(event.dataState);
  };

  const getRowEdit = (row) =>
    editedRows[row.notificationWaveCarrierId] || {};

  const setRowEdit = (id, patch) => {
    setEditedRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), ...patch },
    }));
  };

  const [rowSaveError, setRowSaveError] = React.useState({});

  const handleSaveRow = async (row) => {
    const edits = getRowEdit(row);
    const sendMode = edits.sendMode ?? row.sendMode;
    const carrier = carrierMap[row.carrierId];
    if (sendMode === "OnCarrierRelease" && carrier?.releaseStatus === "Released") {
      setRowSaveError((prev) => ({
        ...prev,
        [row.notificationWaveCarrierId]:
          "Cannot use On Carrier Release on a carrier that is already Released.",
      }));
      return;
    }
    setRowSaveError((prev) => {
      const next = { ...prev };
      delete next[row.notificationWaveCarrierId];
      return next;
    });
    const payload = {
      notificationWaveCarrierId: row.notificationWaveCarrierId,
      sendMode,
      scheduledSendAt: edits.scheduledSendAt ?? row.scheduledSendAt,
      brokerTemplateId: edits.brokerTemplateId ?? row.brokerTemplateId,
    };
    await updateMutation.mutateAsync(payload);
    setEditedRows((prev) => {
      const next = { ...prev };
      delete next[row.notificationWaveCarrierId];
      return next;
    });
    await refetchCarriers();
  };

  const handleCancelRow = async (row) => {
    await updateMutation.mutateAsync({
      notificationWaveCarrierId: row.notificationWaveCarrierId,
      cancel: true,
    });
    await refetchCarriers();
  };

  const handleDispatch = async (row) => {
    await updateMutation.mutateAsync({
      notificationWaveCarrierId: row.notificationWaveCarrierId,
      dispatch: true,
    });
    await refetchCarriers();
  };

  const handleAddCarriers = async () => {
    if (selectedCarrierIds.length === 0) return;
    await scheduleMutation.mutateAsync(
      selectedCarrierIds.map((carrierId) => ({
        carrierId,
        sendMode: "Immediate",
        scheduledSendAt: null,
      }))
    );
    setSelectedCarrierIds([]);
    await refetchCarriers();
  };

  const toggleCarrierSelection = (carrierId) => {
    setSelectedCarrierIds((prev) =>
      prev.includes(carrierId)
        ? prev.filter((id) => id !== carrierId)
        : [...prev, carrierId]
    );
  };

  const SendModeCell = (props) => {
    const row = props.dataItem;
    const edits = getRowEdit(row);
    const currentMode = edits.sendMode ?? row.sendMode ?? "Immediate";
    const carrier = carrierMap[row.carrierId];
    const carrierReleased = carrier?.releaseStatus === "Released";

    // OnCarrierRelease is only meaningful when the carrier hasn't been released yet.
    const sendModeOptions = SEND_MODES.map((m) =>
      m.value === "OnCarrierRelease" && carrierReleased
        ? { ...m, disabled: true }
        : m
    );

    const selected =
      sendModeOptions.find((m) => m.value === currentMode) || sendModeOptions[0];

    return (
      <td>
        <DropDownList
          data={sendModeOptions}
          textField="text"
          dataItemKey="value"
          value={selected}
          itemRender={(li, itemProps) => {
            if (itemProps.dataItem.disabled) {
              return React.cloneElement(
                li,
                {
                  style: { ...li.props.style, color: "#aaa", cursor: "not-allowed" },
                  onClick: (e) => e.stopPropagation(),
                },
                li.props.children
              );
            }
            return li;
          }}
          onChange={(e) => {
            if (e.target.value?.disabled) return;
            setRowEdit(row.notificationWaveCarrierId, {
              sendMode: e.target.value.value,
            });
          }}
          style={{ width: "160px" }}
        />
        {currentMode === "OnCarrierRelease" && (
          <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>
            {carrierReleased
              ? `Carrier already Released. Switch to Immediate or Scheduled.`
              : `Fires when ${carrier?.carrierName ?? "this carrier"} is marked Released.`}
          </div>
        )}
      </td>
    );
  };

  const ScheduledAtCell = (props) => {
    const row = props.dataItem;
    const edits = getRowEdit(row);
    const currentMode = edits.sendMode ?? row.sendMode ?? "Immediate";
    const isScheduled = currentMode === "Scheduled";
    const value = edits.scheduledSendAt
      ? new Date(edits.scheduledSendAt)
      : row.scheduledSendAt
      ? new Date(row.scheduledSendAt)
      : null;

    return (
      <td>
        <DateTimePicker
          disabled={!isScheduled}
          value={value}
          onChange={(e) =>
            setRowEdit(row.notificationWaveCarrierId, {
              scheduledSendAt: e.target.value,
            })
          }
          format="MM/dd/yyyy HH:mm"
          style={{ width: "190px" }}
        />
      </td>
    );
  };

  const TemplateCell = (props) => {
    const row = props.dataItem;
    const edits = getRowEdit(row);
    const currentId = edits.brokerTemplateId ?? row.brokerTemplateId;
    const selected =
      templateOptions.find((t) => t.value === currentId) || null;

    return (
      <td>
        <DropDownList
          data={templateOptions}
          textField="text"
          dataItemKey="value"
          value={selected}
          defaultItem={{ text: "Select template", value: null }}
          onChange={(e) =>
            setRowEdit(row.notificationWaveCarrierId, {
              brokerTemplateId: e.target.value.value,
            })
          }
          style={{ width: "180px" }}
        />
      </td>
    );
  };

  const StatusCell = (props) => (
    <td>
      <StatusBadge status={props.dataItem.status} />
    </td>
  );

  const ReleaseStatusCell = (props) => {
    const carrier = carrierMap[props.dataItem.carrierId];
    return <td>{carrier?.releaseStatus ?? "-"}</td>;
  };

  const ActionsCell = (props) => {
    const row = props.dataItem;
    const isPending = row.status === "Pending";
    const currentMode =
      (getRowEdit(row).sendMode ?? row.sendMode ?? "Immediate");
    const isImmediate = currentMode === "Immediate";
    const hasEdits = Object.keys(getRowEdit(row)).length > 0;

    const saveErr = rowSaveError[row.notificationWaveCarrierId];

    return (
      <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {hasEdits && (
          <Button
            themeColor="primary"
            size="small"
            onClick={() => handleSaveRow(row)}
            disabled={updateMutation.isLoading}
          >
            Save
          </Button>
        )}
        {isPending && (
          <Button
            themeColor="error"
            size="small"
            onClick={() => handleCancelRow(row)}
            disabled={updateMutation.isLoading}
          >
            Cancel
          </Button>
        )}
        {isPending && isImmediate && (
          <Button
            themeColor="success"
            size="small"
            onClick={() => handleDispatch(row)}
            disabled={updateMutation.isLoading}
          >
            Dispatch
          </Button>
        )}
        {saveErr && (
          <div style={{ color: "#c4230a", fontSize: 11, flexBasis: "100%" }}>
            {saveErr}
          </div>
        )}
      </td>
    );
  };

  if (carriersLoading || carrierListLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ marginBottom: 8 }}>Broker Comms by Carrier</h3>

      <ErrorBanner handler={scheduleMutation} />
      <ErrorBanner handler={updateMutation} />

      {availableCarriers.length > 0 && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: 4,
            padding: 12,
            marginBottom: 16,
            background: "#fafafa",
          }}
        >
          <strong>+ Add Carriers</strong>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 8,
              marginBottom: 8,
            }}
          >
            {availableCarriers.map((c) => {
              const checked = selectedCarrierIds.includes(c.carrierId);
              return (
                <label
                  key={c.carrierId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    cursor: "pointer",
                    padding: "2px 8px",
                    border: checked
                      ? "1px solid #0072c6"
                      : "1px solid #ccc",
                    borderRadius: 12,
                    background: checked ? "#e8f0fe" : "#fff",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCarrierSelection(c.carrierId)}
                    style={{ cursor: "pointer" }}
                  />
                  {c.carrierName}
                </label>
              );
            })}
          </div>
          <Button
            themeColor="primary"
            onClick={handleAddCarriers}
            disabled={
              selectedCarrierIds.length === 0 || scheduleMutation.isLoading
            }
          >
            {scheduleMutation.isLoading ? "Adding..." : "Confirm Add"}
          </Button>
        </div>
      )}

      <Grid
        sortable={true}
        pageable={true}
        data={gridData}
        {...dataState}
        onDataStateChange={dataStateChange}
      >
        <Column field="carrierName" title="Carrier" />
        <Column
          title="Release Status"
          cell={ReleaseStatusCell}
          sortable={false}
        />
        <Column title="Send Mode" cell={SendModeCell} sortable={false} />
        <Column
          title="Scheduled At"
          cell={ScheduledAtCell}
          sortable={false}
        />
        <Column title="Template" cell={TemplateCell} sortable={false} />
        <Column title="Status" cell={StatusCell} sortable={false} />
        <Column
          title="Actions"
          cell={ActionsCell}
          sortable={false}
          width={200}
        />
      </Grid>
    </div>
  );
};

export default BrokerCommsByCarrierGrid;
